/* Kashira cart page — mobile swipe-left row actions (spec 16 "Mobile cart page").
   Progressive enhancement ONLY. Every row already renders quiet MOVE TO WISHLIST
   and REMOVE text actions inside the details cell; those stay put and remain the
   keyboard / assistive-tech path. The tray this script injects is a pointer-only
   shortcut whose buttons simply forward a click to those same controls, so it is
   marked aria-hidden + tabindex="-1" and never duplicates anything in the a11y
   tree.

   Gesture rules:
     · touch only, all listeners passive — preventDefault is never called, so
       vertical page scroll and every click keep working
     · threshold-armed: the gesture must travel 12px and be clearly horizontal
       and leftward before the row follows the finger; anything else is handed
       back to the scroller
     · prefers-reduced-motion => the script does not initialise at all; the
       inline actions are already present and reachable
     · above 750px nothing binds (the tray CSS is mobile-scoped) */
(() => {
  if (window.__kashiraCartSwipe) return;
  window.__kashiraCartSwipe = true;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MOBILE = window.matchMedia('(max-width: 749px)');
  const ARM_PX = 12; // gesture must travel this far before we claim it
  const OPEN_PX = 110; // tray width — matches .kashira-row-tray in CSS
  const COMMIT_PX = 45; // release past this opens, otherwise it snaps shut

  let openRow = null;

  const setX = (row, x) => row.style.setProperty('--kashira-row-x', `${x}px`);

  const closeRow = (row) => {
    if (!row) return;
    row.classList.remove('kashira-row-open');
    setX(row, 0);
    if (openRow === row) openRow = null;
  };

  const openRowNow = (row) => {
    if (openRow && openRow !== row) closeRow(openRow);
    row.classList.add('kashira-row-open');
    setX(row, -OPEN_PX);
    openRow = row;
  };

  /* The tray mirrors the row's real controls — it never performs the action
     itself, it clicks the original button so all existing behaviour (wishlist
     store write, UNDO toast, collapse motion) runs exactly once, unchanged. */
  const buildTray = (row) => {
    const wishlistBtn = row.querySelector('.kashira-move-wishlist');
    // Skip removes the platform forbids (can_remove hides the wrapper) —
    // the tray must not rebuild a control the row deliberately doesn't offer.
    const removeBtn = row.querySelector('cart-remove-button:not(.hidden) .kashira-cart-remove');
    if (!wishlistBtn && !removeBtn) return null;

    /* .cart-item is a <tr>, so the tray must be a <td> — a <div> child of a row
       is invalid HTML and a parser is free to drop or foster-parent it. The
       cell is absolutely positioned (and display:none above 750px), so it never
       adds a column to the table layout, and aria-hidden keeps it out of the
       a11y tree — the inline actions remain the only exposed controls. */
    const tray = document.createElement('td');
    tray.className = 'kashira-row-tray';
    tray.setAttribute('aria-hidden', 'true');

    const proxy = (source, modifier) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.tabIndex = -1;
      button.className = `kashira-row-tray__button${modifier}`;
      button.textContent = source.textContent.trim();
      button.addEventListener('click', () => {
        closeRow(row);
        source.click();
      });
      tray.appendChild(button);
    };

    if (wishlistBtn) proxy(wishlistBtn, '');
    if (removeBtn) proxy(removeBtn, ' kashira-row-tray__button--remove');
    return tray;
  };

  const bindRow = (row) => {
    if (row.__kashiraSwipeBound) return;
    const tray = buildTray(row);
    if (!tray) return;
    row.__kashiraSwipeBound = true;
    row.classList.add('kashira-row-swipeable');
    row.appendChild(tray);

    let startX = 0;
    let startY = 0;
    let dx = 0;
    let mode = null; // null = undecided, 'swipe', 'ignore'

    row.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length !== 1 || !MOBILE.matches || REDUCED.matches) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        dx = 0;
        mode = null;
      },
      { passive: true }
    );

    row.addEventListener(
      'touchmove',
      (event) => {
        if (mode === 'ignore' || event.touches.length !== 1 || !MOBILE.matches || REDUCED.matches) return;
        dx = event.touches[0].clientX - startX;
        const dy = event.touches[0].clientY - startY;

        if (mode === null) {
          if (Math.abs(dx) < ARM_PX && Math.abs(dy) < ARM_PX) return;
          // Vertical intent belongs to the page scroller, not to us.
          mode = Math.abs(dx) > Math.abs(dy) ? 'swipe' : 'ignore';
          if (mode === 'ignore') return;
          row.classList.add('kashira-row-dragging');
        }

        const base = row.classList.contains('kashira-row-open') ? -OPEN_PX : 0;
        setX(row, Math.min(0, Math.max(-OPEN_PX, base + dx)));
      },
      { passive: true }
    );

    const onEnd = () => {
      if (mode !== 'swipe') {
        mode = null;
        return;
      }
      row.classList.remove('kashira-row-dragging');
      const wasOpen = row.classList.contains('kashira-row-open');
      const travelled = wasOpen ? -OPEN_PX + dx : dx;
      if (travelled < -COMMIT_PX) openRowNow(row);
      else closeRow(row);
      mode = null;
    };

    row.addEventListener('touchend', onEnd, { passive: true });
    row.addEventListener(
      'touchcancel',
      () => {
        row.classList.remove('kashira-row-dragging');
        closeRow(row);
        mode = null;
      },
      { passive: true }
    );
  };

  const bindAll = () => {
    if (REDUCED.matches) return;
    document.querySelectorAll('main[data-template="cart"] cart-items .cart-item').forEach(bindRow);
  };

  // Any tap outside the open row closes it; the tap itself is never swallowed.
  document.addEventListener(
    'click',
    (event) => {
      if (openRow && !openRow.contains(event.target)) closeRow(openRow);
    },
    { passive: true }
  );

  /* Both preferences can change after load — a rotation or a resize crosses the
     750px breakpoint, and the OS motion setting can be toggled mid-session.
     Observe them so the behaviour follows instead of freezing at page load. */
  const onQueryChange = () => {
    closeRow(openRow);
    bindAll();
  };
  const observe = (query) => {
    if (typeof query.addEventListener === 'function') query.addEventListener('change', onQueryChange);
    else if (typeof query.addListener === 'function') query.addListener(onQueryChange);
  };
  observe(MOBILE);
  observe(REDUCED);

  const boot = () => {
    const root = document.querySelector('main[data-template="cart"] cart-items');
    if (!root) return;
    bindAll();
    // Dawn re-renders the whole line list on every cart mutation.
    new MutationObserver(() => {
      openRow = null;
      bindAll();
    }).observe(root, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
